import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { apiClient } from "@/App";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  ArrowRight,
  ShoppingBag,
  Tag,
  Star,
} from "lucide-react";

const Cart = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await apiClient.get("/cart");
      setCart(response.data);
    } catch (error) {
      console.error("Cart fetch error:", error);
      toast.error("Failed to load cart");
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await apiClient.delete(`/cart/${productId}`);
      toast.success("Item removed");
      fetchCart();
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const response = await apiClient.post("/cart/checkout");
      toast.success(`Order placed! +${response.data.points_earned} points earned`);
      navigate(`/orders/${response.data.order_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Checkout failed");
    } finally {
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const pointsToEarn = Math.round((cart?.total || 0) * 5);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8" data-testid="cart-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl">Your Cart</h1>
          </div>
          <p className="text-muted-foreground">
            {cart?.items?.length || 0} items in your cart
          </p>
        </motion.div>

        {!cart?.items?.length ? (
          // Empty Cart
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card>
              <CardContent className="text-center py-16">
                <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="font-heading font-semibold text-xl mb-2">Your cart is empty</h3>
                <p className="text-muted-foreground mb-6">
                  Looks like you haven't added anything yet.
                </p>
                <Button onClick={() => navigate("/wear")} data-testid="shop-now-btn">
                  Start Shopping
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-2 space-y-4"
            >
              {cart.items.map((item, index) => (
                <motion.div
                  key={`${item.product_id}-${item.size}-${item.color}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card data-testid={`cart-item-${item.product_id}`}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Product Image */}
                        <Link
                          to={`/product/${item.product_id}`}
                          className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0"
                        >
                          <img
                            src={item.product?.image_url}
                            alt={item.product?.name}
                            className="w-full h-full object-cover"
                          />
                        </Link>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <Link to={`/product/${item.product_id}`}>
                            <h3 className="font-medium truncate hover:text-primary">
                              {item.product?.name}
                            </h3>
                          </Link>
                          <div className="flex gap-2 mt-1 text-sm text-muted-foreground">
                            {item.size && <span>Size: {item.size}</span>}
                            {item.color && <span>• Color: {item.color}</span>}
                          </div>
                          <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">
                                Qty: {item.quantity}
                              </span>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="font-heading font-bold text-lg">
                                ${(item.product?.price * item.quantity).toFixed(2)}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFromCart(item.product_id)}
                                className="text-destructive hover:text-destructive"
                                data-testid={`remove-${item.product_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>

            {/* Order Summary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle className="font-heading">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${cart.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shipping</span>
                    <span className="text-secondary">Free</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="font-heading font-semibold">Total</span>
                    <span className="font-heading font-bold text-xl">
                      ${cart.total.toFixed(2)}
                    </span>
                  </div>

                  {/* Points Banner */}
                  <div className="p-3 bg-accent/10 rounded-xl flex items-center gap-2">
                    <Star className="w-5 h-5 text-accent" />
                    <span className="text-sm">
                      Earn <strong>{pointsToEarn}</strong> BE Points with this order
                    </span>
                  </div>
                </CardContent>
                <CardFooter className="flex-col gap-4">
                  <Button
                    className="w-full rounded-full py-6"
                    size="lg"
                    onClick={checkout}
                    disabled={checkingOut}
                    data-testid="checkout-btn"
                  >
                    {checkingOut ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Checkout
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => navigate("/wear")}
                  >
                    Continue Shopping
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Cart;
